import { displayValue } from '../../lib/oem-display';
import { RecordGallery } from './record-gallery';
import { runtimeMode } from '../../lib/auth';
import { MediaDialog } from './media-dialog';
import { ArrowRight, Check, ChevronRight, FlaskConical, LockKeyhole, ZoomIn } from 'lucide-react';
import { useState } from 'react';
import type { OemRecord, OemWorkspace } from '../../lib/oem-client';
import { SensoryFeedback } from './sensory-feedback';
import {
  applicableSensoryAnswers,
  composeSensoryComment,
  type SensoryAnswers,
  type SensoryPreset,
} from './sensory-feedback-model';
export function SampleReview({
  workspace,
  exactRecordId,
  canDecide,
  busy,
  onDecision,
  audience,
}: {
  readonly workspace: OemWorkspace;
  readonly exactRecordId?: string | undefined;
  readonly canDecide: boolean;
  readonly busy: boolean;
  readonly audience: 'staff' | 'customer';
  readonly onDecision: (
    record: OemRecord,
    criteria: string[],
    comment: string,
    disposition: string,
  ) => Promise<void>;
}) {
  const samples = workspace.records.filter((r) => r.kind === 'sample');
  const [selected, setSelected] = useState(exactRecordId ?? samples[0]?.id ?? '');
  const [criteria, setCriteria] = useState<string[]>(runtimeMode === 'fixture' ? ['texture'] : []);
  const [comment, setComment] = useState(
    runtimeMode === 'fixture'
      ? 'อยากได้เนื้อสัมผัสของ A และทิศทางกลิ่นของ B โดยขอดูตัวอย่างใหม่ก่อนตัดสินใจ'
      : '',
  );
  const [scope, setScope] = useState(false);
  const [zoom, setZoom] = useState(false);
  const sample = samples.find((r) => r.id === selected);
  const [disposition, setDisposition] = useState(runtimeMode === 'fixture' ? 'partial' : '');
  const [preset, setPreset] = useState<SensoryPreset>('skincare');
  const [answers, setAnswers] = useState<SensoryAnswers>({});
  const received =
    runtimeMode === 'fixture' ||
    workspace.decisions.some((d) => d.recordId === sample?.id && d.action === 'receive');
  const composed = composeSensoryComment(comment, preset, audience === 'customer' ? answers : {});
  const canSend =
    !busy &&
    canDecide &&
    !!sample &&
    criteria.length > 0 &&
    !!disposition &&
    received &&
    composed.valid;
  function selectSample(id: string) {
    if (id === selected) return;
    setSelected(id);
    setCriteria(runtimeMode === 'fixture' ? ['texture'] : []);
    setComment('');
    setDisposition(runtimeMode === 'fixture' ? 'partial' : '');
    setPreset('skincare');
    setAnswers({});
  }
  const decision = (recordId: string, criterion?: string) =>
    workspace.decisions
      .filter(
        (d) =>
          d.recordId === recordId &&
          d.action === 'decide_sensory' &&
          (!criterion || d.criteria.includes(criterion)),
      )
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
  const criterionCodes = [
    ...new Set(
      samples.flatMap((r) =>
        Array.isArray(r.content.criteria)
          ? r.content.criteria.filter((v): v is string => typeof v === 'string')
          : [],
      ),
    ),
  ];
  const criterionLabel = (code: string) =>
    displayValue(
      workspace.records.find((r) => r.kind === 'criterion' && r.content.code === code)?.content
        .label ??
        ({ texture: 'เนื้อสัมผัส', fragrance: 'กลิ่น' } as Record<string, string>)[code] ??
        code,
    );
  const labels: Record<string, string> = {
    accepted: 'ยอมรับคุณสมบัติที่เลือก',
    partial: 'ยอมรับบางส่วน',
    revision_requested: 'ขอทบทวนใหม่',
    rejected: 'ยังไม่รับ',
  };
  return (
    <>
      <div className="crumb">
        โครงการ <ChevronRight /> {workspace.project.brand || 'ยังไม่ระบุแบรนด์'} <ChevronRight />{' '}
        รายละเอียดฉบับที่เลือก
      </div>
      <div className="page-title">
        <div>
          <div className="eyebrow">ประเมินตัวอย่างสินค้า</div>
          <h1>เปรียบเทียบตัวอย่าง</h1>
          <p className="muted wrap">
            {workspace.project.label} · {workspace.project.brand}
          </p>
        </div>
        <span className="chip green">
          <Check />
          {runtimeMode === 'fixture'
            ? 'ความคิดเห็นบางส่วน'
            : (labels[decision(selected)?.disposition ?? ''] ?? 'ยังไม่มีการตัดสินใจ')}
        </span>
      </div>
      <div className="card action-card">
        <div className="action-icon">
          <FlaskConical />
        </div>
        <div className="action-text">
          <h2>
            {runtimeMode === 'fixture'
              ? 'รอบนี้ ต้องการปรับเรื่องกลิ่น'
              : 'ความคิดเห็นบนตัวอย่างฉบับนี้'}
          </h2>
          <p>
            {runtimeMode === 'fixture'
              ? 'เนื้อสัมผัส A รับแล้วในขอบเขตที่ระบุ · กลิ่นยังเปิดให้ทบทวน'
              : 'แต่ละเกณฑ์ต้องมีการตัดสินใจโดยผู้ได้รับสิทธิ์ ดูความคิดเห็นและสถานะของตัวอย่างที่เลือก'}
          </p>
        </div>
        <span className="chip">
          <LockKeyhole />
          เฉพาะคุณสมบัติที่เลือกประเมิน
        </span>
      </div>
      <div className="sectionhead">
        <h2>
          {runtimeMode === 'fixture'
            ? 'เปรียบเทียบ A / B / C'
            : 'เปรียบเทียบตัวอย่างที่ได้รับสิทธิ์'}
        </h2>
        <span className="muted">ตัวอย่างและล็อตแยกกัน</span>
      </div>
      <div className="grid3">
        {samples.map((r, i) => (
          <article key={r.id} className={`card sample-card ${selected === r.id ? 'selected' : ''}`}>
            <RecordGallery
              key={r.id}
              projectId={workspace.project.id}
              recordIds={[r.id]}
              label={`${displayValue(r.content.sampleCode)} v${r.version}`}
            />
            <div className="sample-info">
              <button className="text-button" onClick={() => selectSample(r.id)}>
                <h3>{displayValue(r.content.label)}</h3>
              </button>
              <div className="version">
                {displayValue(r.content.sampleCode)}
                <br />
                {displayValue(r.content.summary)}
                <br />
                {displayValue(r.content.preparationCode)}
              </div>
              <span className={`chip ${i === 0 ? 'green' : i === 1 ? 'amber' : ''}`}>
                {displayValue(
                  runtimeMode === 'fixture'
                    ? (r.content.status ?? 'รอความคิดเห็น')
                    : (workspace.decisions
                        .filter((d) => d.recordId === r.id && d.action === 'decide_sensory')
                        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0]
                        ?.disposition ?? 'รอความคิดเห็น'),
                )}
              </span>
            </div>
          </article>
        ))}
      </div>
      {samples.length === 0 ? (
        <p className="card">ยังไม่มีตัวอย่างที่ส่งให้คุณ · กรุณาติดต่อผู้ประสานงาน</p>
      ) : null}
      <div className="sectionhead">
        <h2>ผลเปรียบเทียบ</h2>
      </div>
      <section className="card">
        <div className="compare-row">
          <strong>เกณฑ์</strong>
          {samples.map((r) => (
            <strong key={r.id}>{displayValue(r.content.label)}</strong>
          ))}
        </div>
        {(runtimeMode === 'fixture'
          ? [
              ['เนื้อสัมผัส', 'รับบางส่วน', 'อยากปรับ', 'ยังไม่ประเมิน'],
              ['กลิ่น', 'ยังไม่รับ', 'ชอบทิศทาง', 'ยังไม่ประเมิน'],
            ]
          : criterionCodes.map((code) => [
              criterionLabel(code),
              ...samples.map(
                (r) => labels[decision(r.id, code)?.disposition ?? ''] ?? 'ยังไม่ประเมิน',
              ),
            ])
        ).map((row) => (
          <div key={row[0]} className="compare-row">
            {row.map((v, i) => (
              <span key={i} className={i ? 'metric' : ''}>
                {v}
              </span>
            ))}
          </div>
        ))}
        <div className="scope">
          <strong>
            {runtimeMode === 'fixture'
              ? 'ขอบเขตที่รับ: เนื้อสัมผัสของ SMP-A-001'
              : `ขอบเขตที่เลือก: ${displayValue(sample?.content.sampleCode ?? 'ยังไม่มีตัวอย่าง')}`}
          </strong>
          <br />
          {runtimeMode === 'fixture'
            ? 'ความชอบกลิ่น B ยังเป็นข้อเสนอให้พัฒนา ไม่ใช่การอนุมัติสูตรผสมใหม่'
            : 'ผล sensory แต่ละเกณฑ์ผูกกับตัวอย่างและการเตรียมชุดนี้ การเปลี่ยนสูตรต้องทบทวนตามผลกระทบ'}
        </div>
        <fieldset className="scope-fields">
          <legend>เกณฑ์ที่ต้องการรับสำหรับ {displayValue(sample?.content.sampleCode ?? '')}</legend>
          {(runtimeMode === 'fixture'
            ? [
                ['texture', 'เนื้อสัมผัส'],
                ['fragrance', 'กลิ่น'],
              ]
            : (Array.isArray(sample?.content.criteria)
                ? sample.content.criteria.filter((v): v is string => typeof v === 'string')
                : []
              ).map((code) => [code, criterionLabel(code)])
          ).map(([value, label]) => (
            <label key={value}>
              <input
                type="checkbox"
                checked={criteria.includes(value ?? '')}
                onChange={(e) =>
                  setCriteria(
                    e.target.checked
                      ? [...criteria, value ?? '']
                      : criteria.filter((c) => c !== value),
                  )
                }
              />
              {label}
            </label>
          ))}
        </fieldset>
        {runtimeMode === 'connected' ? (
          <section aria-label="ประวัติความคิดเห็นของตัวอย่างนี้">
            <h3>ความคิดเห็นบนตัวอย่างนี้</h3>
            {workspace.decisions
              .filter((d) => d.recordId === selected && d.action === 'decide_sensory')
              .map((d, i) => (
                <article key={`${d.createdAt}:${i}`} className="scope">
                  <p>
                    {d.criteria.map(criterionLabel).join(' · ')} ·{' '}
                    {labels[d.disposition ?? ''] ?? d.disposition}
                  </p>
                  <p>{d.comment}</p>
                  <p className="version">
                    {selected} · {d.createdAt}
                  </p>
                </article>
              ))}
          </section>
        ) : null}
        <label className="field">
          ผลในเกณฑ์ที่เลือก
          <select value={disposition} onChange={(e) => setDisposition(e.target.value)}>
            <option value="">เลือกผลการตัดสินใจ</option>
            <option value="partial">ยอมรับบางส่วน</option>
            <option value="accepted">ยอมรับคุณสมบัติที่เลือก</option>
            <option value="revision_requested">ขอทบทวนใหม่</option>
            <option value="rejected">ยังไม่รับ</option>
          </select>
        </label>
        {audience === 'customer' && sample ? (
          <SensoryFeedback
            preset={preset}
            answers={answers}
            disabled={busy || !canDecide || !received}
            onPreset={(next) => {
              setPreset(next);
              setAnswers((current) => applicableSensoryAnswers(next, current));
            }}
            onAnswers={setAnswers}
          />
        ) : null}
        {audience === 'customer' && sample && !canDecide ? (
          <p className="fineprint">คุณดูความคิดเห็นได้ แต่ยังไม่มีสิทธิ์ส่งผลประเมินตัวอย่างนี้</p>
        ) : null}
        {audience === 'customer' && sample && !received ? (
          <p className="fineprint">ต้องยืนยันการรับตัวอย่างก่อนส่งความคิดเห็น</p>
        ) : null}
        <label className="field">
          ความคิดเห็นสำหรับรอบถัดไป · ลูกค้าเห็นข้อความนี้
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} />
        </label>
        <p role={composed.valid ? 'status' : 'alert'} className="fineprint">
          {composed.remaining >= 0
            ? `ข้อความรวมคะแนนและข้อสังเกตเหลือ ${composed.remaining} ตัวอักษร`
            : `ข้อความรวมเกิน ${-composed.remaining} ตัวอักษร กรุณาลดความคิดเห็นหรือข้อสังเกตก่อนส่ง`}
        </p>
      </section>
      <div className="footer-actions">
        <div>
          <strong>บันทึกเป็นข้อเสนอแก้ไข</strong>
          <p className="mock-note">
            {runtimeMode === 'fixture'
              ? 'F-001 v4 ยังเป็นร่าง ไม่ใช่สูตรที่รับแล้ว'
              : 'ความคิดเห็นผูกกับรหัสตัวอย่าง การเตรียม และเกณฑ์ที่เลือกเท่านั้น'}
          </p>
        </div>
        <div className="actions">
          <button className="action secondary" onClick={() => setScope(true)}>
            ดูขอบเขต
          </button>
          <button
            className="action"
            disabled={!canSend}
            onClick={() =>
              canSend && sample && void onDecision(sample, criteria, composed.text, disposition)
            }
          >
            ส่งความคิดเห็น
            <ArrowRight />
          </button>
        </div>
      </div>
      <p className="fineprint">
        ต้องออนไลน์และรอผลยืนยันจากเซิร์ฟเวอร์ • ภาพแนวคิดเป็นข้อมูลสังเคราะห์
      </p>
      {scope ? (
        <MediaDialog
          className="oem-dialog"
          label="ขอบเขต sensory"
          onDismiss={() => setScope(false)}
        >
          <h2>ขอบเขต sensory</h2>
          <p>
            การตัดสินใจอ้างถึง {displayValue(sample?.content.sampleCode)} v{sample?.version}{' '}
            และเกณฑ์ที่เลือกเท่านั้น สูตร การผลิต และผลทดสอบต้องผ่านผู้รับผิดชอบแยกกัน
          </p>
          <button className="action" onClick={() => setScope(false)}>
            ปิด
          </button>
        </MediaDialog>
      ) : null}
      {zoom ? (
        <MediaDialog
          className="oem-dialog media-dialog"
          label="ภาพแนวคิด"
          onDismiss={() => setZoom(false)}
        >
          <h2>
            <ZoomIn />
            ภาพแนวคิด
          </h2>
          <img
            src="/concepts/cream-packaging-concept.png"
            alt="ภาพแนวคิด AI ไม่มีภาพตัวอย่างจริง"
          />
          <button className="action" onClick={() => setZoom(false)}>
            ปิดภาพ
          </button>
        </MediaDialog>
      ) : null}
    </>
  );
}
